import { Box, Stack, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import {
  getDeepViewLineageEntry,
  isGapRuleBaPrefix,
} from "../../deepViewFieldMap";

const monoSx = {
  fontFamily: "monospace",
  fontSize: "12px",
  fontWeight: 700,
  color: "#7B1FA2",
};

const sectionLabelSx = {
  fontSize: "11px",
  fontWeight: 500,
  color: "#00000099",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
};

/** Formatted API values arrive as strings like "£240.00" or "$1,537.10" */
const parseFormattedNumber = (value: any): number => {
  if (value === null || value === undefined) return 0;
  const numeric = String(value).replace(/[^0-9.-]/g, "");
  const parsed = Number(numeric);
  return isNaN(parsed) ? 0 : parsed;
};

/** Everything that is not part of the number is the currency symbol */
const extractCurrencySymbol = (value: any): string =>
  String(value ?? "")
    .replace(/[0-9.,\s-]/g, "")
    .trim();

const formatAmount = (amount: number, symbol: string) =>
  `${symbol}${amount.toLocaleString("en-US", { maximumFractionDigits: 2 })}`;

interface Props {
  /** API key of the column the user clicked, e.g. "total_income_usd" */
  columnKey?: string;
}

const GapRule = ({ columnKey }: Props) => {
  const deepViewData = useSelector(
    (state: any) => state?.deepView?.deepViewData,
  );
  const currencyData = useSelector(
    (state: any) => state?.deepView?.currencyData,
  );

  const lineageEntry = getDeepViewLineageEntry(deepViewData, columnKey);
  const period = deepViewData?.period;
  const rawRows = deepViewData?.values?.rows;
  const rows = Array.isArray(rawRows) ? rawRows[0] : rawRows;

  // Only fields produced by a gap rule have anything to show here
  if (!isGapRuleBaPrefix(lineageEntry?.ba_prefix)) return null;

  // The deal commission already carries a symbol — cross-check it against the
  // currency code the values row reports, so a mismatch cannot pass unnoticed.
  const cpaCurrencyCode = rows?.cpa_currency_code;
  const codeSymbol = (currencyData ?? []).find(
    (currency: any) => currency?.currency_code === cpaCurrencyCode,
  )?.currency_symbol;
  const dealCpaFormatted = lineageEntry?.fmt_deal_cpa_commission;
  const dealCpaSymbol = extractCurrencySymbol(dealCpaFormatted);
  const hasCurrencyMismatch =
    !!codeSymbol && !!dealCpaSymbol && codeSymbol !== dealCpaSymbol;
  const currencySymbol = codeSymbol ?? dealCpaSymbol;

  const qftd = Number(rows?.period_unifier_qftd ?? 0);
  const dealCpaAmount = parseFormattedNumber(dealCpaFormatted);
  const cpaIncome = qftd * dealCpaAmount;

  // const netRevenue = parseFormattedNumber(rows?.fmt_period_unifier_net_revenue);
  // const revsharePercent = Number(rows?.deal_percent_commission ?? 0) / 100;
  // const revshare = netRevenue * revsharePercent;

  // const totalIncome = cpaIncome + revshare;
  // const currencyRate = period?.currency_rate;
  // const totalIncomeUsd = Number(
  //   String(rows?.period_dashboard_total_income_usd ?? "").replace(/[$,]/g, ""),
  // );
  // const percentLabel = `${(revsharePercent * 100).toLocaleString("en-US", {
  //   maximumFractionDigits: 2,
  // })}%`;
  const dateRange = [period?.fmt_start_date, period?.fmt_end_date]
    .filter(Boolean)
    .join(" → ");

  const inputRows: any = [
    { label: "QFTD (from unifier)", value: qftd.toLocaleString("en-US") },
    {
      label: `Deal CPA Commission (${cpaCurrencyCode ?? ""})`,
      value: dealCpaFormatted ?? "-",
    },
    {
      label: `CPA Income = QFTD × ${dealCpaFormatted ?? ""}`,
      value: formatAmount(cpaIncome, currencySymbol),
      emphasis: true,
    },
    // {
    //   label: `Net Revenue (${cpaCurrencyCode ?? ""})`,
    //   value: rows?.fmt_period_unifier_net_revenue ?? "-",
    // },
    // { label: "Deal RevShare %", value: percentLabel },
    // {
    //   label: `RevShare = Net Rev × ${percentLabel}`,
    //   value: formatAmount(revshare, currencySymbol),
    //   emphasis: true,
    // },
    // {
    //   label: `Total Income (${cpaCurrencyCode ?? ""})`,
    //   value: formatAmount(totalIncome, currencySymbol),
    //   emphasis: true,
    // },
    // {
    //   label: `Currency Rate (${cpaCurrencyCode ?? ""} → USD)`,
    //   value: currencyRate != null ? `× ${currencyRate}` : "-",
    //   valueColor: "#C62828",
    // },
    // {
    //   label: "Total Income (USD)",
    //   value: formatAmount(totalIncomeUsd, "$"),
    //   emphasis: true,
    // },
  ];

  return (
    <Box mb={1}>
      <Stack direction="row" mb={1}>
        <Typography sx={{ ...sectionLabelSx, width: 110, flexShrink: 0 }}>
          Rule
        </Typography>
        <Typography sx={monoSx}>
          {lineageEntry?.gap_rule_expression_sql ?? "-"}
        </Typography>
      </Stack>

      <Stack direction="row" mb={1}>
        <Typography sx={{ ...sectionLabelSx, width: 110, flexShrink: 0 }}>
          Description
        </Typography>
        <Typography sx={{ fontSize: "12px", color: "#000000DE" }}>
          {lineageEntry?.gap_rule_description ?? "-"}
        </Typography>
      </Stack>

      <Box
        sx={{
          border: "1px solid #9C27B080",
          borderRadius: "8px",
          backgroundColor: "#9C27B00A",
          p: 1,
          mb: 1.5,
        }}
      >
        <Typography sx={{ ...sectionLabelSx, color: "#7B1FA2", mb: 0.5 }}>
          {lineageEntry?.gap_rule_title ?? "Formula"}
        </Typography>
        <Typography sx={monoSx}>
          {lineageEntry?.gap_rule_expression_sql ?? "-"}
        </Typography>
      </Box>

      <Typography sx={{ ...sectionLabelSx, mb: 0.5 }}>
        Actual inputs {dateRange && `— ${dateRange}`}
      </Typography>

      <Box mb={1.5}>
        <Stack
          direction="row"
          sx={{ borderBottom: "1px solid #E0E0E0", py: 0.5, px: 1 }}
        >
          <Typography sx={{ ...sectionLabelSx, flexGrow: 1 }}>
            Variable
          </Typography>
          <Typography sx={{ ...sectionLabelSx, textAlign: "right" }}>
            Value
          </Typography>
        </Stack>

        {inputRows.map((row: any) => (
          <Stack
            key={row.label}
            direction="row"
            alignItems="center"
            sx={{
              py: 0.75,
              px: 1,
              backgroundColor: row.emphasis ? "#FAFAFA" : "transparent",
              borderBottom: "1px solid #F0F0F0",
            }}
          >
            <Typography
              sx={{
                flexGrow: 1,
                textAlign: "right",
                pr: 2,
                fontSize: "12px",
                fontWeight: row.emphasis ? 700 : 400,
                color: row.emphasis ? "#000000DE" : "#00000099",
              }}
            >
              {row.label}
            </Typography>
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: row.emphasis ? 700 : 400,
                color: row.emphasis
                  ? "#000000DE"
                  : (row.valueColor ?? "#7B1FA2"),
                whiteSpace: "nowrap",
              }}
            >
              {row.value}
            </Typography>
          </Stack>
        ))}

        {hasCurrencyMismatch && (
          <Typography
            sx={{
              fontSize: "11px",
              color: "#C62828",
              mt: 0.5,
              px: 1,
            }}
          >
            Currency mismatch — the deal commission is in {dealCpaSymbol} but
            the CPA currency code is {cpaCurrencyCode} ({codeSymbol}). The
            amounts below may be wrong.
          </Typography>
        )}
      </Box>

      {/* <Typography sx={{ ...sectionLabelSx, mb: 0.5 }}>
        Currency conversion
      </Typography> */}

      {/* <Stack direction="row" alignItems="center" spacing={1}>
        <Box
          sx={{
            flex: 1,
            border: "1px solid #E0E0E0",
            borderRadius: "8px",
            backgroundColor: "#FFFFFF",
            p: 1,
            textAlign: "center",
          }}
        >
          <Typography sx={{ ...sectionLabelSx, fontSize: "9px" }}>
            Raw value
          </Typography>
          <Typography
            sx={{ fontSize: "16px", fontWeight: 500, color: "#000000DE" }}
          >
            {formatAmount(totalIncome, currencySymbol)}
          </Typography>
          <Typography sx={{ ...sectionLabelSx, fontSize: "9px" }}>
            {cpaCurrencyCode ?? ""}
          </Typography>
        </Box>

        <ArrowForwardIcon sx={{ color: "#ED6C02", fontSize: 14 }} />

        <Box
          sx={{
            flex: 1,
            border: "1px solid #ED6C0280",
            borderRadius: "8px",
            backgroundColor: "#ED6C020A",
            p: 1,
            textAlign: "center",
          }}
        >
          <Typography
            sx={{ ...sectionLabelSx, fontSize: "9px", color: "#ED6C02" }}
          >
            Rate
          </Typography>
          <Typography
            sx={{ fontSize: "12px", fontWeight: 700, color: "#ED6C02" }}
          >
            1 {cpaCurrencyCode ?? ""} = {currencyRate ?? "-"} USD
          </Typography>
        </Box>

        <ArrowForwardIcon sx={{ color: "#ED6C02", fontSize: 14 }} />

        <Box
          sx={{
            flex: 1,
            border: "1px solid #E0E0E0",
            borderRadius: "8px",
            backgroundColor: "#FFFFFF",
            p: 1,
            textAlign: "center",
          }}
        >
          <Typography sx={{ ...sectionLabelSx, fontSize: "9px" }}>
            Dashboard value
          </Typography>
          <Typography
            sx={{ fontSize: "16px", fontWeight: 500, color: "#7B1FA2" }}
          >
            {formatAmount(totalIncomeUsd, "$")}
          </Typography>
          <Typography sx={{ ...sectionLabelSx, fontSize: "9px" }}>
            USD
          </Typography>
        </Box>
      </Stack> */}
    </Box>
  );
};

export default GapRule;
